import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { ScrollArea } from "./ui/scroll-area";
import { 
  LayoutDashboard, History, Building2, Calendar, 
  AlertTriangle, Settings, LogOut, Database, Menu, X, BookOpen, Zap, Briefcase, TrendingUp,
  ChevronDown, ChevronRight, FileText, BarChart3, Target, Activity, LineChart, Brain, Network, Play, Gem, Scale, FlaskConical, Radio, Newspaper, Sparkles, GraduationCap, Radar, Telescope, CalendarClock, HandCoins, Anchor, Gauge, ScanSearch
} from "lucide-react";
import { useState } from "react";
import { PipelineAlertBanner } from "./PipelineAlertBanner";

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    "13f": true,  // Default expanded
    "companies": true, // Default expanded
    "intelligence": true, // Default expanded
  });

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Navigation structure with sections
  const navSections = [
    {
      id: "13f",
      label: "13F Holdings",
      icon: FileText,
      items: [
        { path: "/", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/runs", icon: History, label: "Pipeline Runs" },
        { path: "/funds", icon: Building2, label: "Fund Explorer" },
        { path: "/fund-performance", icon: BarChart3, label: "Fund Performance" },
        { path: "/quarters", icon: Calendar, label: "Quarter Explorer" },
        { path: "/buy-sell", icon: Zap, label: "Buy/Sell Events" },
        { path: "/data-quality", icon: AlertTriangle, label: "Data Quality" },
        { path: "/config", icon: Settings, label: "Configuration" },
      ]
    },
    {
      id: "companies",
      label: "Companies",
      icon: Briefcase,
      items: [
        { path: "/company-profiles", icon: Building2, label: "Profiles" },
        { path: "/stock-perf", icon: TrendingUp, label: "Performance" },
        { path: "/consensus", icon: Target, label: "Consensus" },
        { path: "/alpha", icon: Activity, label: "Alpha" },
        { path: "/barra", icon: LineChart, label: "Barra Model" },
      ]
    },
    {
      id: "intelligence",
      label: "Fund Intelligence",
      icon: Brain,
      items: [
        { path: "/skills", icon: Activity, label: "Skills & Timing" },
        { path: "/centrality", icon: Network, label: "Centrality" },
        { path: "/curve-model", icon: BarChart3, label: "Curve Model" },
        { path: "/curves-pipeline", icon: Play, label: "Curves Pipeline" },
        { path: "/kensys-targets", icon: Target, label: "Kensys Targets" },
        { path: "/nowcasting", icon: Activity, label: "Nowcasting" },
        { path: "/entry-anchor", icon: Anchor, label: "Entry Anchoring" },
        { path: "/stock-read", icon: ScanSearch, label: "Stock Read" },
        { path: "/kfm", icon: Gauge, label: "Kensys Score (mockup)" },
        { path: "/hidden-gems", icon: Gem, label: "Hidden Gems" },
        { path: "/ls-book", icon: Scale, label: "L/S Book" },
        { path: "/strategy-lab", icon: FlaskConical, label: "Strategy Lab" },
        { path: "/rebound-radar", icon: Radar, label: "Rebound Radar" },
        { path: "/discovery-scouts", icon: Telescope, label: "Discovery Scouts" },
        { path: "/earnings-alpha", icon: CalendarClock, label: "Earnings Alpha" },
        { path: "/dividend-alpha", icon: HandCoins, label: "Emergent Dividend" },
      ]
    },
    {
      id: "live",
      label: "Live",
      icon: Radio,
      path: "/live",
    },
    {
      id: "digest",
      label: "Digest",
      icon: Newspaper,
      path: "/digest",
    },
    {
      id: "thematic",
      label: "Thematic",
      icon: Sparkles,
      path: "/thematic",
    },
    {
      id: "signals",
      label: "Signals",
      icon: Activity,
      path: "/signals",
    },
    {
      id: "fund-grades",
      label: "Fund Grades",
      icon: GraduationCap,
      path: "/fund-grades",
    },
    {
      id: "stats",
      label: "Signal Stats",
      icon: BarChart3,
      path: "/stats",
    },
    {
      id: "database",
      label: "Database Management",
      icon: Database,
      path: "/database",
    },
    {
      id: "docs",
      label: "Documentation",
      icon: BookOpen,
      path: "/docs",
    },
  ];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform lg:translate-x-0 lg:static ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-sm flex items-center justify-center">
                <Database className="w-4 h-4 text-primary" />
              </div>
              <div>
                <h1 className="font-semibold text-sm">13F Control Tower</h1>
                <p className="text-xs text-muted-foreground">Data Platform</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-muted rounded-sm"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navSections.map((section) => {
                // Single page section (no subsections)
                if (section.path) {
                  return (
                    <NavLink
                      key={section.id}
                      to={section.path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `sidebar-nav-item ${isActive ? "active" : ""}`
                      }
                      data-testid={`nav-${section.id}`}
                    >
                      <section.icon className="w-4 h-4" />
                      {section.label}
                    </NavLink>
                  );
                }

                // Section with subsections
                const isExpanded = expandedSections[section.id];
                return (
                  <div key={section.id} className="space-y-1">
                    {/* Section header */}
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 rounded-sm transition-colors"
                      data-testid={`section-${section.id}`}
                    >
                      <section.icon className="w-4 h-4" />
                      <span className="flex-1 text-left font-medium">{section.label}</span>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {/* Subsection items */}
                    {isExpanded && (
                      <div className="ml-4 pl-2 border-l border-border/50 space-y-1">
                        {section.items.map((item) => (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.path === "/"}
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) =>
                              `sidebar-nav-item text-xs ${isActive ? "active" : ""}`
                            }
                            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          >
                            <item.icon className="w-3.5 h-3.5" />
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 bg-muted rounded-sm flex items-center justify-center text-xs font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{user?.email}</p>
                  <p className="text-xs text-muted-foreground">Admin</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="rounded-sm"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <PipelineAlertBanner />
        {/* Top bar */}
        <header className="h-16 bg-card border-b border-border flex items-center px-4 lg:px-6">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden p-2 hover:bg-muted rounded-sm mr-4"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
